import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetItemsPriceFluctuationReport,
  useGetPriceFluctuationData,
} from "src/api/reports";
import { useGetBranches } from "src/api/generic";
import { useGetItemsList } from "src/api/purchasing/items";
import DateRangeInput from "src/shared/components/date-range";
import ReportsIcon from "src/assets/svg-icons/side-bar/reports.svg";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import {
  downloadAsXLS,
  formatNumber,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";

type DetailRow = {
  purchase_id: number;
  purchase_ref: string;
  branch_name: string;
  due_date: string;
  item_name: string;
  unit_name: string;
  quantity: number;
  net_total: number;
  fluctuation_percent: number;
};

function unwrapPriceRows(res: unknown): DetailRow[] {
  if (!res || typeof res !== "object") return [];
  const o = res as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object") {
    const rows = (inner as Record<string, unknown>).rows;
    if (Array.isArray(rows)) return rows as DetailRow[];
  }
  if (Array.isArray(o.rows)) return o.rows as DetailRow[];
  return [];
}

type ItemDetailReportFilters = {
  startDate: string;
  endDate: string;
  branchId: string;
  itemId: string;
};

const ItemDetailReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterText, setFilterText] = useState("");

  const formValues = useMemo((): ItemDetailReportFilters => {
    return {
      startDate:
        searchParams.get("start_date") ||
        moment().startOf("month").format("yyyy-MM-DD"),
      endDate:
        searchParams.get("end_date") ||
        moment().endOf("month").format("yyyy-MM-DD"),
      branchId: searchParams.get("branch_id") || "",
      itemId: searchParams.get("item_id") || "",
    };
  }, [searchParams]);

  const { control, setValue } = useForm<ItemDetailReportFilters>({
    values: formValues,
  });

  const { startDate, endDate, branchId: branchIdParam, itemId: itemIdParam } =
    formValues;

  const { data: branchesData } = useGetBranches();
  const { data: itemsData } = useGetItemsList();

  const branchesHavePermission =
    branchesData?.data.filter((branch) =>
      specificBranchHasPermission("download_third_eye_report", branch.id)
    ) || [];

  const sortedItems = useMemo(() => {
    return [...(itemsData?.data || [])].sort((a: any, b: any) =>
      a.name?.localeCompare(b.name)
    );
  }, [itemsData]);

  const itemIdValid = itemIdParam.length > 0;
  const branchIdNum = branchIdParam ? Number(branchIdParam) : NaN;
  const branchFilter = Number.isFinite(branchIdNum) ? [branchIdNum] : [];

  const { data: reportResponse, isFetching } = useGetPriceFluctuationData({
    start_date: startDate,
    end_date: endDate,
    item_id: itemIdParam,
    branches: branchFilter,
    enabled: itemIdValid,
  });

  const { refetch: refetchPriceFluctuationReport, isFetching: isDownloadingReport } =
    useGetItemsPriceFluctuationReport({
      start_date: startDate,
      end_date: endDate,
      item_id: itemIdParam,
      branches: branchFilter,
      enabled: false,
    });

  const rows = useMemo(
    () => unwrapPriceRows(reportResponse),
    [reportResponse]
  );

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.purchase_ref.toLowerCase().includes(q) ||
        r.branch_name.toLowerCase().includes(q) ||
        r.item_name.toLowerCase().includes(q)
    );
  }, [rows, filterText]);

  const syncDatesToUrl = (start: string, end: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("start_date", start);
    next.set("end_date", end);
    setSearchParams(next);
  };

  const syncItemToUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("item_id", id);
    else next.delete("item_id");
    setSearchParams(next);
  };

  const syncBranchToUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("branch_id", id);
    else next.delete("branch_id");
    setSearchParams(next);
  };

  const downloadReportsPermission = oneBranchHasPermission(
    "download_third_eye_report"
  );

  const handleDownload = () => {
    if (!itemIdValid) return;
    void refetchPriceFluctuationReport().then((res) => {
      if (res.data != null) {
        downloadAsXLS(`\uFEFF${res.data}`, "price-fluctuation-report");
      }
    });
  };

  return (
    <Box gap={2} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
        mb={2}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={ReportsIcon} width={"32px"} alt="" />
        </Box>
        <Typography fontSize={"32px"} lineHeight={"38.4px"} color={"#101828"}>
          {t("itemDetailReport")}
        </Typography>
      </Stack>

      <Stack
        spacing={2}
        direction={"row"}
        alignItems={"center"}
        flexWrap={"wrap"}
        mb={2}
      >
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {t("itemDetailReport")}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        flexWrap={"wrap"}
        gap={2}
        mb={2}
      >
        <Controller
          name="branchId"
          control={control}
          render={({ field }) => (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="item-detail-branch" shrink>
                {generalT("branch")}
              </InputLabel>
              <Select
                {...field}
                labelId="item-detail-branch"
                label={generalT("branch")}
                onChange={(e) => {
                  const v = e.target.value as string;
                  field.onChange(v);
                  syncBranchToUrl(v);
                }}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{t("allBranches")}</em>
                </MenuItem>
                {branchesHavePermission.map((branch) => (
                  <MenuItem key={branch.id} value={String(branch.id)}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Controller
          name="itemId"
          control={control}
          render={({ field }) => (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="item-detail-item" shrink>
                {t("item")}
              </InputLabel>
              <Select
                {...field}
                labelId="item-detail-item"
                label={t("item")}
                onChange={(e) => {
                  const v = e.target.value as string;
                  field.onChange(v);
                  syncItemToUrl(v);
                }}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{t("selectItem")}</em>
                </MenuItem>
                {sortedItems.map((item: any) => (
                  <MenuItem key={item.id} value={String(item.id)}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Box sx={{ minWidth: 250 }}>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <DateRangeInput
                startDate={
                  field.value ? moment(field.value) : null
                }
                startDateId="item-detail-start"
                endDate={
                  formValues.endDate ? moment(formValues.endDate) : null
                }
                endDateId="item-detail-end"
                onDatesChange={(arg: {
                  startDate: moment.Moment | null;
                  endDate: moment.Moment | null;
                }) => {
                  const s = arg.startDate?.format("yyyy-MM-DD");
                  const e = arg.endDate?.format("yyyy-MM-DD");
                  if (s && e) {
                    field.onChange(s);
                    setValue("endDate", e);
                    syncDatesToUrl(s, e);
                  }
                }}
              />
            )}
          />
        </Box>

        <TextField
          size="small"
          placeholder={t("search")}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ minWidth: 200, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#98A2B3" }} fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          variant="outlined"
          color="tertiary"
          onClick={handleDownload}
          loading={isDownloadingReport}
          disabled={!itemIdValid || !downloadReportsPermission}
          sx={{ height: 40, minWidth: 120 }}
        >
          {generalT("download")}
        </LoadingButton>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #EAECF0",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box bgcolor={"#F9FAFB"} px={2} py={1.5}>
          <Typography fontWeight={600} color={"#101828"}>
            {t("itemDetailReport")}
          </Typography>
        </Box>
        {!itemIdValid ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">{t("selectItemToViewReport")}</Typography>
          </Box>
        ) : isFetching ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }}>
                    {t("purchaseRef")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }}>
                    {generalT("branch")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }}>
                    {t("dueDate")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }}>
                    {t("itemName")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }}>
                    {t("unit")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }} align="right">
                    {t("quantity")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }} align="right">
                    {t("netTotal")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }} align="right">
                    {t("fluctuationPercent")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475467" }} align="center" width={72}>
                    {generalT("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography color="text.secondary" align="center" py={2}>
                        {generalT("noData")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row, index) => {
                    const pct = row.fluctuation_percent;
                    const color =
                      pct > 0 ? "#12B76A" : pct < 0 ? "#F04438" : "#667085";
                    return (
                      <TableRow key={`${row.purchase_ref}-${index}`}>
                        <TableCell>{row.purchase_ref}</TableCell>
                        <TableCell>{row.branch_name}</TableCell>
                        <TableCell>{row.due_date}</TableCell>
                        <TableCell>{row.item_name}</TableCell>
                        <TableCell>{row.unit_name}</TableCell>
                        <TableCell align="right">
                          {formatNumber(row.quantity)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(row.net_total)}
                        </TableCell>
                        <TableCell align="right" sx={{ color, fontWeight: 500 }}>
                          {formatNumber(pct)}%
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t("viewPurchase")}>
                            <IconButton
                              size="small"
                              aria-label={t("viewPurchase")}
                              onClick={() =>
                                navigate(`/purchasing/${row.purchase_id}`)
                              }
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default ItemDetailReportPage;
